import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, X, GlobeHemisphereWest, Check } from "@phosphor-icons/react";
import { COUNTRIES } from "../config/countries";
import { useCountry } from "../hooks/useCountry";
import { getStreamInfoForTz } from "../utils/streamTime";

export const CountryPicker = ({ trigger = "auto", onClose }) => {
    const { country, setCountry, ready, guess } = useCountry();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [preview, setPreview] = useState(null); // hovered/highlighted country for preview

    // Auto-open on first ever visit if there's no stored country
    useEffect(() => {
        if (!ready) return;
        if (trigger === "auto" && !country) {
            const t = setTimeout(() => setOpen(true), 350);
            return () => clearTimeout(t);
        }
        if (trigger === "manual") setOpen(true);
    }, [ready, country, trigger]);

    const guessedDefault = useMemo(() => guess?.() || null, [guess]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COUNTRIES;
        return COUNTRIES.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.code.toLowerCase().includes(q) ||
                c.tz.toLowerCase().includes(q)
        );
    }, [query]);

    const choose = (c) => {
        setCountry(c);
        setOpen(false);
        setQuery("");
        if (onClose) onClose();
    };

    const close = () => {
        // Don't allow closing without choosing on first-time (forced) open
        if (!country && trigger === "auto") return;
        setOpen(false);
        if (onClose) onClose();
    };

    // Live preview of the converted stream time
    const previewCountry = preview || guessedDefault || filtered[0] || null;
    const previewInfo = previewCountry ? getStreamInfoForTz(previewCountry.tz) : null;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={close}
                    data-testid="country-picker"
                >
                    <motion.div
                        initial={{ scale: 0.94, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.94, y: 20, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-zinc-950 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-white/10 relative">
                            {country && (
                                <button
                                    type="button"
                                    onClick={close}
                                    className="absolute top-4 right-4 text-white/40 hover:text-white p-1"
                                    aria-label="Close"
                                    data-testid="country-picker-close"
                                >
                                    <X size={18} />
                                </button>
                            )}
                            <div className="flex items-center gap-3 mb-2">
                                <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                                    <GlobeHemisphereWest size={18} weight="bold" className="text-cyan-300" />
                                </div>
                                <div>
                                    <div className="eyebrow text-cyan-300/80">Localize your stream time</div>
                                    <div className="font-display text-white text-lg">Where are you watching from?</div>
                                </div>
                            </div>
                            <p className="text-white/55 text-sm mt-3">
                                Pick your country — the schedule across the site will show stream times in your local timezone, with the next stream live-counted.
                            </p>
                        </div>

                        {/* Live preview strip */}
                        {previewInfo && previewCountry && (
                            <div
                                className="px-6 py-4 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-b border-white/5 flex items-center gap-4 flex-wrap"
                                data-testid="country-picker-preview"
                            >
                                <span className="text-3xl leading-none">{previewCountry.flag}</span>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="eyebrow text-white/60">Next stream in {previewCountry.name}</div>
                                    <div className="font-display text-white text-lg mt-0.5">
                                        {previewInfo.dayLabel} · {previewInfo.timeLabel}{" "}
                                        <span className="text-white/40 font-mono text-sm">{previewInfo.tzShort}</span>
                                    </div>
                                </div>
                                <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 font-mono text-xs text-white/70">
                                    {previewInfo.countdown}
                                </div>
                            </div>
                        )}

                        {/* Search */}
                        <div className="px-6 pt-5">
                            <div className="relative">
                                <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                                <input
                                    type="text"
                                    autoFocus
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search countries..."
                                    className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-white/30 focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/15 transition"
                                    data-testid="country-search-input"
                                />
                            </div>
                        </div>

                        {/* Country list */}
                        <div className="px-3 pt-2 pb-3 max-h-[44vh] overflow-y-auto" data-testid="country-list">
                            {filtered.length === 0 ? (
                                <div className="text-center text-white/40 text-sm py-10">No matches.</div>
                            ) : (
                                <ul className="space-y-0.5">
                                    {filtered.map((c) => {
                                        const isSelected = country?.code === c.code;
                                        return (
                                            <li key={c.code}>
                                                <button
                                                    type="button"
                                                    onClick={() => choose(c)}
                                                    onMouseEnter={() => setPreview(c)}
                                                    onFocus={() => setPreview(c)}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition group ${
                                                        isSelected
                                                            ? "bg-cyan-500/10 border border-cyan-500/30"
                                                            : "hover:bg-white/5 border border-transparent"
                                                    }`}
                                                    data-testid={`country-option-${c.code}`}
                                                >
                                                    <span className="text-xl leading-none w-7 text-center">{c.flag}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-white text-sm font-medium truncate">{c.name}</div>
                                                        <div className="text-[11px] text-white/40 font-mono truncate">{c.tz}</div>
                                                    </div>
                                                    {isSelected && <Check size={14} weight="bold" className="text-cyan-300" />}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        {!country && (
                            <div className="px-6 py-4 border-t border-white/10 text-[11px] text-white/40 uppercase tracking-[0.2em] font-mono">
                                Pick a country to enter the site
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
